import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { aboutTimeline } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import SEO, { organizationSchema, breadcrumbSchema } from '../components/SEO';

const About = () => {
  const { tx, language } = useLanguage();
  const fr = language === 'fr';
  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title="About Us"
        description="Learn about Total Composites — a Quebec-based manufacturer of premium composite truck camper shells. Our story, timeline, and mission to build the lightest and strongest expedition campers."
        path="/about"
        keywords="Total Composites about, composite camper manufacturer, expedition camper company, truck camper maker Canada, FRP camper shells"
        jsonLd={[
          organizationSchema,
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' }
          ])
        ]}
      />
      {/* Header */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Notre histoire' : 'Our Story'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'À propos de TOTAL COMPOSITES' : 'About TOTAL COMPOSITES'}
          </h1>
          <p className="text-gray-400 max-w-xl leading-relaxed">
            {fr ? "Le principal fournisseur nord-américain de panneaux composites haut de gamme et de systèmes de campeur porté, né d'une passion pour le voyage d'expédition." : "North America's leading supplier of premium composite panels and truck camper systems, born from a lifelong passion for expedition travel."}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                {fr ? "Conçu par l'expérience. Construit pour l'aventure." : 'Engineered from Experience. Built for Adventure.'}
              </h2>
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                {fr ? (
                  <>
                    <p>Chez TOTAL COMPOSITES, nous sommes fiers d&apos;être le choix numéro un en Amérique du Nord pour des kits de carrosseries de camion prêts à assembler de haute qualité, conçus sur mesure pour vous. Nos kits sont adaptés à une utilisation sur route et hors route dans tous les climats, avec un processus d&apos;assemblage rapide de seulement 2 à 3 jours.</p>
                    <p>Grâce à notre construction sans transfert thermique, vous profiterez d&apos;une véritable expérience de camping quatre saisons. Nos carrosseries composites sont conçues avec des matériaux haut de gamme soigneusement sélectionnés en Allemagne pour garantir des performances et une durabilité à long terme.</p>
                    <p>Chaque composant est rigoureusement choisi et inspecté, garantissant une utilisation fiable et sans problème pendant des années. Notre production se déroule dans une usine familiale en Chine, conformément aux normes européennes. Cette installation nous permet de maintenir des pratiques de fabrication de haute qualité et d&apos;offrir une valeur exceptionnelle.</p>
                    <p>Avec un volume de production annuel de plus de 800 carrosseries, et ce chiffre en expansion, TOTAL COMPOSITES continue de répondre à la demande croissante en Amérique du Nord.</p>
                  </>
                ) : (
                  <>
                    <p>At TOTAL COMPOSITES, we&apos;re proud to be North America&apos;s top choice for high-quality, ready-to-assemble truck body kits made just for you. Our kits are designed for both on-road and off-road use in any climate, with a quick assembly process that takes only 2-3 days.</p>
                    <p>Thanks to our thermal transfer-free construction, you&apos;ll enjoy a true four-season camping experience. Our composite truck bodies are engineered with premium, carefully sourced materials from Germany to ensure long-lasting performance and durability.</p>
                    <p>Each component is rigorously chosen and inspected, guaranteeing reliable, trouble-free use for years to come. Our production takes place in a family-owned factory in China, adhering to European standards. This facility allows us to maintain high-quality manufacturing practices and deliver exceptional value.</p>
                    <p>With an annual production volume of over 800 truck bodies and expanding, TOTAL COMPOSITES continues to meet growing demand across North America.</p>
                  </>
                )}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-gray-50 rounded-xl p-5 text-center">
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1">{fr ? 'Siège social' : 'Headquarters'}</div>
                  <div className="text-lg font-black text-gray-900">Victoria, BC</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 text-center">
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1">{fr ? 'Normes' : 'Standards'}</div>
                  <div className="text-lg font-black text-gray-900">{fr ? 'Européennes' : 'European'}</div>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/i0nud105_image.png"
                alt="Total Composites builders assembling an expedition kit"
                className="w-full object-cover rounded-xl shadow-lg aspect-square"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
              {fr ? 'Notre fondateur' : 'Our Founder'}
            </p>
            <h2 className="text-3xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
              Andreas Schwall
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Text */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                {fr ? (
                  <>
                    <p>Andreas Schwall est un expatrié allemand qui s&apos;est installé en Colombie-Britannique, au Canada, en 2001. Avant TOTAL COMPOSITES, il était ébéniste, paramédical et constructeur de camions d&apos;expédition haut de gamme. À son arrivée au Canada, il a géré des ateliers d&apos;ébénisterie et dirigé sa propre entreprise de menuiserie — mais sa fascination pour les véhicules d&apos;expédition ne l&apos;a jamais quitté.</p>
                    <p>« Tout ce que je trouvais aux États-Unis au début des années 2000, c&apos;était des VR médiocres. Je voulais construire le mien. » Ce qui a commencé comme une obsession personnelle a évolué en quelque chose de bien plus grand. En 2015, Andreas a importé un kit d&apos;une usine que son père — un ingénieur allemand en plastiques — avait établie en Chine, construite selon les normes allemandes avec des composants allemands. Il l&apos;a modifié pour en faire un campeur porté.</p>
                    <p>Des amis l&apos;ont remarqué. Les communautés en ligne ont suivi sa construction et voulaient la même chose. L&apos;intérêt a fait boule de neige. Andreas a affiné la conception, optimisant l&apos;épaisseur des murs et la performance thermique pour l&apos;usage campeur porté plutôt que cargo — et TOTAL COMPOSITES est devenu une entreprise de plusieurs millions de dollars.</p>
                    <p>Des décennies d&apos;expérience de voyage réel — incluant cinq traversées du Sahara en moto, un voyage de 3,5 mois au Sénégal et des expéditions arctiques en hiver — continuent de guider chaque décision de conception. Le résultat : des habitats qui performent là où les autres échouent.</p>
                  </>
                ) : (
                  <>
                    <p>Andreas Schwall is a German expat who relocated to British Columbia, Canada in 2001. Before TOTAL COMPOSITES, he was a furniture-maker, a paramedic, and a builder of high-end expedition trucks. When he arrived in Canada, he managed cabinet shops and ran his own woodworking business &mdash; but his fascination with expedition vehicles never left him.</p>
                    <p>&ldquo;Everything I could find in the U.S. in the early 2000s was a shitty RV. I wanted to build my own.&rdquo; What started as a personal obsession evolved into something much larger. In 2015, Andreas imported a kit from a factory his father &mdash; a German plastics engineer &mdash; had established in China, built to German standards with German components. He modified it into a truck camper.</p>
                    <p>Friends noticed. Online communities followed his build and wanted in. Interest snowballed. Andreas refined the design, optimizing wall thickness and thermal performance for camper use rather than cargo &mdash; and TOTAL COMPOSITES grew into a multimillion-dollar operation.</p>
                    <p>Decades of real-world travel experience &mdash; including five Sahara crossings by motorbike, a 3.5-month journey to Senegal, and Arctic expeditions in winter &mdash; continue to drive every design decision. The result: habitats that perform where others fail.</p>
                  </>
                )}
              </div>
            </div>
            {/* Photos — gallery aligned with article */}
            <div className="flex flex-col gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/gz8enekp_image.png"
                alt="Andreas Schwall inside a Total Composites camper interior"
                className="rounded-2xl shadow-lg w-full object-cover"
                style={{ height: '260px' }}
              />
              <div className="grid grid-cols-2 gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/tvw3ds3q_image.png"
                  alt="Andreas Schwall with the Total Composites team at the workshop"
                  className="rounded-2xl shadow-lg w-full object-cover"
                  style={{ height: '160px' }}
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/2zi6xmpq_image.png"
                  alt="Andreas Schwall in front of a Total Composites Bobcat camper"
                  className="rounded-2xl shadow-lg w-full object-cover"
                  style={{ height: '160px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-12 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Notre parcours' : 'Our Journey'}
          </h2>
          <div className="relative">
            <div className="absolute left-16 top-0 bottom-0 w-px bg-gray-200" />
            <div className="space-y-10">
              {aboutTimeline.map((item, i) => (
                <div key={item.year || item.title} className="flex items-start gap-8">
                  <div
                    className="flex-shrink-0 w-16 text-right"
                  >
                    <span
                      className="text-lg font-black"
                      style={{ color: '#E8520A', fontFamily: 'Inter, sans-serif' }}
                    >
                      {item.year}
                    </span>
                  </div>
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow-md mt-1"
                      style={{ backgroundColor: '#E8520A' }}
                    />
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed pt-0.5">{tx(item.event)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Rejoignez notre réseau grandissant' : 'Join Our Growing Network'}
          </h2>
          <p className="text-gray-500 mb-8 text-sm">
            {fr ? "Devenez partenaire TOTAL COMPOSITES ou contactez notre équipe d'ingénierie." : 'Become a TOTAL COMPOSITES partner or get in touch with our engineering team.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
              style={{ backgroundColor: '#E8520A' }}
            >
              {fr ? 'CONTACTEZ-NOUS' : 'CONTACT US'} <ArrowRight size={16} />
            </Link>
            <Link
              to="/partners"
              className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-gray-800 rounded-full text-sm border-2 border-gray-300 transition-all duration-200 hover:border-gray-600"
            >
              {fr ? 'Voir les partenaires' : 'View Partners'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
